import { IDocumentScheme } from '@models/document/IDocumentScheme';
import {
    IChanges,
    IColumn,
    IDocumentAttachmentsTable,
    IDocumentField,
    IDocumentTable,
    IRow,
} from '@models/Forms/IForms';
import { IFieldElem } from '@models/IFormData';
import type { IRequiredMessageField, RequiredDocumentControl } from './requiredMessage';

/**
 * Обход дерева формы документа (`scheme.form.view`).
 *
 * Дерево: `view.rows.row[] → IRow → columns[] → IColumn`, где `IColumn` содержит
 * value-контролы (fields/calcs/dicts/abooks/autoCompletes/tables) и рекурсивно —
 * вложенные `blocks[].rows.row[]` и `rows[]`. Топ-уровневый таб =
 * `view.rows.row[i].columns[0].blocks[0]` (см. DocumentTabBuilder).
 *
 * ВИДИМОСТЬ НАСЛЕДУЕТСЯ. Row/Column/Block при `visibilityRules=false` рендерят `<></>`
 * (см. Row/Column/Block.tsx — useFormulaState + ранний возврат), поэтому их потомки
 * размонтированы и НЕ валидируются. Обход накапливает `visibilityChain` —
 * visibilityRules всех контейнеров-предков (таб-блок → row → column → вложенный block).
 * Resolver валидирует контрол только если ВСЕ правила цепочки видимы.
 */

/**
 * Контрол, который надо валидировать.
 * - `field` — полный набор правил (required/pattern/длина/checkDate);
 * - `required` — только required (dict/abook/autoComplete/calc);
 * - `table` — кумулятивная валидация строк и вложенных таблиц по схеме;
 * - `attachments` — блок вложений в форме: обязательность по формуле `required`.
 *
 * ВНИМАНИЕ: `attachments` не привязан к `fields[key]` — его значение живёт по
 * фиксированному пути формы `files` (см. ATTACHMENTS_FORM_PATH). Потребители,
 * которые начинают с поиска `fields[control.key]`, обязаны разбирать этот вид
 * ДО такого поиска.
 */
/** Ближайший именованный блок формы над контролом (секция внутри вкладки). */
export interface IControlSection {
    id: string;
    name: string;
}

/** Где контрол лежит в форме: подписи вкладки и секции для навигации пользователя. */
interface IControlPlace {
    tabName?: string;
    section?: IControlSection;
}

export type ValidatableControl = (
    | { kind: 'field'; key: string; field: IDocumentField; visibilityChain: string[]; tabId: string }
    | {
          kind: 'required';
          key: string;
          controlType: RequiredDocumentControl;
          control: {
              key: string;
              required: string;
              visibilityRules: string;
              readonly?: string;
              name?: string;
          } & IRequiredMessageField;
          visibilityChain: string[];
          tabId: string;
      }
    | { kind: 'table'; key: string; table: IDocumentTable; visibilityChain: string[]; tabId: string }
    | {
          kind: 'attachments';
          key: string;
          table: IDocumentAttachmentsTable;
          visibilityChain: string[];
          tabId: string;
      }
) &
    IControlPlace;

/**
 * Рекурсивно обойти колонки поддерева, накапливая цепочку видимости предков.
 * `parentChain` — visibilityRules контейнеров выше текущих строк (таб-блок и т.д.).
 */
export function* walkColumns(
    rows: IRow[] | undefined,
    parentChain: string[] = [],
    section?: IControlSection,
): Generator<{ col: IColumn; chain: string[]; section?: IControlSection }> {
    if (!rows) return;
    for (const row of rows) {
        const rowChain = row.visibilityRules ? [...parentChain, row.visibilityRules] : parentChain;
        for (const col of row.columns ?? []) {
            const colChain = col.visibilityRules ? [...rowChain, col.visibilityRules] : rowChain;
            yield { col, chain: colChain, section };
            for (const block of col.blocks ?? []) {
                const blockChain = block.visibilityRules ? [...colChain, block.visibilityRules] : colChain;
                const blockSection = block.name ? { id: block.id, name: block.name } : section;
                yield* walkColumns(block.rows?.row, blockChain, blockSection);
            }
            if (col.rows?.length) {
                yield* walkColumns(col.rows, colChain, section);
            }
        }
    }
}

/** Value-контролы одной колонки (с цепочкой видимости) в порядке: поля, затем required-only контролы. */
function controlsOfColumn(
    col: IColumn,
    chain: string[],
    tabId: string,
    withAttachments: boolean,
    place: IControlPlace = {},
): ValidatableControl[] {
    const out: ValidatableControl[] = [];
    for (const field of col.fields ?? []) {
        if (field.key) out.push({ kind: 'field', key: field.key, field, visibilityChain: chain, tabId, ...place });
    }
    // dict/abook/autoComplete/calc дают в Field-слое только required-правило
    const requiredOnly = [
        ...(col.dicts ?? []).map((control) => ({ control, controlType: 'dict' as const })),
        ...(col.abooks ?? []).map((control) => ({ control, controlType: 'abook' as const })),
        ...(col.autoCompletes ?? []).map((control) => ({ control, controlType: 'autoComplete' as const })),
        ...(col.calcs ?? []).map((control) => ({ control, controlType: 'calc' as const })),
    ];
    for (const { control: c, controlType } of requiredOnly) {
        if (c.key) {
            out.push({
                kind: 'required',
                controlType,
                key: c.key,
                control: c,
                visibilityChain: chain,
                tabId,
                ...place,
            });
        }
    }
    // Таблицы: собственная visibilityRules таблицы добавляется в её цепочку видимости.
    for (const t of col.tables ?? []) {
        if (t.key) {
            out.push({
                kind: 'table',
                key: t.key,
                table: t,
                visibilityChain: t.visibilityRules ? [...chain, t.visibilityRules] : chain,
                tabId,
                ...place,
            });
        }
    }
    // Блок вложений монтируется только при заданном scheme.attach.tables (Column.tsx) —
    // без него валидировать нечего.
    if (withAttachments) {
        for (const t of col.attachmentsTables ?? []) {
            const key = t.id || t.key;
            if (key) out.push({ kind: 'attachments', key, table: t, visibilityChain: chain, tabId, ...place });
        }
    }
    return out;
}

/** Схемы, для которых предупреждение про общее поле `files` уже выдано. */
const warnedSchemes = new WeakSet<IDocumentScheme>();

/**
 * Все `attachmentsTable` формы пишут в одно поле `files`: два блока, видимых одновременно,
 * затирают друг друга. Конфигурация обязана разводить их visibilityRules — напоминаем об этом
 * разработчику конфигурации, а не пользователю.
 */
function warnOnSharedAttachmentsValue(scheme: IDocumentScheme, controls: ValidatableControl[]): void {
    if (warnedSchemes.has(scheme)) return;
    const unconditional = controls.filter(
        (control) => control.kind === 'attachments' && control.visibilityChain.length === 0,
    );
    if (unconditional.length > 1) {
        warnedSchemes.add(scheme);
        console.warn(
            'В форме несколько блоков <attachmentsTable> без visibilityRules у предков: ' +
                unconditional.map((control) => control.key).join(', ') +
                '. Все они пишут в одно значение формы "files" и затирают друг друга — ' +
                'разведите блоки взаимоисключающими visibilityRules.',
        );
    }
}

/** Плоский список всех валидируемых контролов документа с цепочкой видимости предков. */
export function collectValidatableControls(scheme: IDocumentScheme): ValidatableControl[] {
    const out: ValidatableControl[] = [];
    const withAttachments = !!scheme.attach?.tables?.length;
    const topRows = scheme.form?.view?.rows?.row ?? [];
    for (const topRow of topRows) {
        // таб = первый блок первой колонки топ-строки; его visibilityRules — корень цепочки
        const block = topRow.columns?.[0]?.blocks?.[0];
        if (!block) continue;
        const tabChain = block.visibilityRules ? [block.visibilityRules] : [];
        for (const { col, chain, section } of walkColumns(block.rows?.row, tabChain)) {
            out.push(
                ...controlsOfColumn(col, chain, `page-${block.id}`, withAttachments, { tabName: block.name, section }),
            );
        }
    }
    if (import.meta.env.DEV) warnOnSharedAttachmentsValue(scheme, out);
    return out;
}

/** Контрол с каскадом invokeChangesOnNew/onEdit для централизованной инициализации. */
export interface InvokeChangeControl {
    key: string;
    changes: IChanges;
    /** Для таблицы дополнительно выполняются changes всех вложенных таблиц. */
    table?: IDocumentTable;
    invokeChangesOnNew: boolean;
    invokeChangesOnEdit: boolean;
    /** Собственная visibilityRules контрола. */
    visibilityRules: string;
    /** visibilityRules контейнеров-предков. */
    visibilityChain: string[];
}

type InvokeChangeCandidate = {
    key: string;
    changes: IChanges;
    invokeChangesOnNew?: boolean;
    invokeChangesOnEdit?: boolean;
    visibilityRules?: string;
};

/**
 * Плоский список контролов с флагами invokeChangesOnNew/onEdit (field/table/dict/abook).
 * Порядок точно повторяет их относительный порядок в `Column.tsx`:
 * вложенные blocks → fields → tables → dicts → abooks → вложенные rows.
 *
 * AutoComplete/Calc исключены — у них нет mount-эффекта invokeChanges (каскад только на ввод).
 */
export function collectInvokeChangeControls(scheme: IDocumentScheme): InvokeChangeControl[] {
    const out: InvokeChangeControl[] = [];

    const visitRows = (rows: IRow[] | undefined, parentChain: string[]) => {
        for (const row of rows ?? []) {
            const rowChain = row.visibilityRules ? [...parentChain, row.visibilityRules] : parentChain;
            for (const col of row.columns ?? []) {
                const chain = col.visibilityRules ? [...rowChain, col.visibilityRules] : rowChain;

                for (const block of col.blocks ?? []) {
                    const blockChain = block.visibilityRules ? [...chain, block.visibilityRules] : chain;
                    visitRows(block.rows?.row, blockChain);
                }

                const candidates: (InvokeChangeCandidate & { table?: IDocumentTable })[] = [
                    ...(col.fields ?? []),
                    ...(col.tables ?? []).map((table) => ({ ...table, table })),
                    ...(col.dicts ?? []),
                    ...(col.abooks ?? []),
                ];
                for (const c of candidates) {
                    if (!c.key) continue;
                    if (!c.invokeChangesOnNew && !c.invokeChangesOnEdit) continue;
                    out.push({
                        key: c.key,
                        changes: c.changes,
                        table: c.table,
                        invokeChangesOnNew: !!c.invokeChangesOnNew,
                        invokeChangesOnEdit: !!c.invokeChangesOnEdit,
                        visibilityRules: c.visibilityRules ?? '',
                        visibilityChain: chain,
                    });
                }

                visitRows(col.rows, chain);
            }
        }
    };

    const topRows = scheme.form?.view?.rows?.row ?? [];
    for (const topRow of topRows) {
        const block = topRow.columns?.[0]?.blocks?.[0];
        if (!block) continue;
        const tabChain = block.visibilityRules ? [block.visibilityRules] : [];
        visitRows(block.rows?.row, tabChain);
    }
    return out;
}

/** Все value-контролы, чтобы invokeChanges флаг на set мог найти changes до mount. */
export function collectChangeControls(scheme: IDocumentScheme): InvokeChangeControl[] {
    return collectValidatableControls(scheme).flatMap<InvokeChangeControl>((control) => {
        if (control.kind === 'field') return [{ key: control.key, changes: control.field.changes, invokeChangesOnNew: false, invokeChangesOnEdit: false, visibilityRules: control.field.visibilityRules, visibilityChain: control.visibilityChain }];
        if (control.kind === 'required') return [{ key: control.key, changes: (control.control as any).changes, invokeChangesOnNew: false, invokeChangesOnEdit: false, visibilityRules: control.control.visibilityRules, visibilityChain: control.visibilityChain }];
        if (control.kind === 'table') return [{ key: control.key, changes: control.table.changes, table: control.table, invokeChangesOnNew: false, invokeChangesOnEdit: false, visibilityRules: control.table.visibilityRules, visibilityChain: control.visibilityChain }];
        return [];
    }).filter((control) => Boolean(control.changes || control.table));
}

/** Ключи всех контролов колонки, несущих значение (для подсветки таба) — включая таблицы. */
function valueKeysOfColumn(col: IColumn): string[] {
    const keys: string[] = [];
    const push = (arr?: { key?: string }[]) => arr?.forEach((c) => c.key && keys.push(c.key));
    push(col.fields);
    push(col.dicts);
    push(col.abooks);
    push(col.autoCompletes);
    push(col.calcs);
    push(col.tables);
    return keys;
}

/**
 * Статический маппинг `tabId (block.id) → Set<field index>` из схемы.
 *
 * Замена рантайм-мутации `includedFields`: индекс берётся из runtime-карты
 * `fields[key].index`. Используется для подсветки невалидного таба
 * (пересечение с `Object.keys(errors.fields)`). Видимость здесь не важна:
 * скрытое поле не даст ошибку, поэтому его индекс в наборе безвреден.
 */
export function buildTabFieldIndex(
    scheme: IDocumentScheme,
    fields: Record<string, IFieldElem>,
): Map<string, Set<number>> {
    const map = new Map<string, Set<number>>();
    const topRows = scheme.form?.view?.rows?.row ?? [];
    for (const row of topRows) {
        const block = row.columns?.[0]?.blocks?.[0];
        if (!block?.id) continue;
        const indices = new Set<number>();
        for (const { col } of walkColumns(block.rows?.row)) {
            for (const key of valueKeysOfColumn(col)) {
                const index = fields[key]?.index;
                if (index !== undefined && index >= 0) indices.add(index);
            }
        }
        map.set(block.id, indices);
    }
    return map;
}
